// Passkeys in the page (PROTOCOL.md §2.2, §4.1): the relay's JSON options turned into WebAuthn calls with the PRF
// extension, and the responses turned back into JSON for the relay. The PRF output stays in the page: it's taken
// from the client extension results and removed before anything is sent.
import { fromB64u, toB64u } from '../shared/b64.ts';
import { prfSalt, random } from '../shared/crypto.ts';

type Bytes = Uint8Array<ArrayBuffer>;
type Json = Record<string, any>; // the relay's options JSON (simplewebauthn's shape)

export interface Ceremony {
  /** What the relay verifies: the credential as JSON, without PRF results. */
  response: Json;
  credentialId: string;
  /** The 32-byte PRF output for PROTOCOL.md's salt, or null when the passkey gave none. */
  prf: Bytes | null;
}

export class PasskeyError extends Error {}

export function passkeysAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && !!navigator.credentials;
}

interface PrfResults {
  prf?: { enabled?: boolean; results?: { first?: ArrayBuffer | ArrayBufferView } };
  [key: string]: unknown;
}

const extensionResults = (cred: PublicKeyCredential) => cred.getClientExtensionResults() as PrfResults;

function prfOutput(cred: PublicKeyCredential): Bytes | null {
  const first = extensionResults(cred).prf?.results?.first;
  if (!first) return null;
  const bytes = first instanceof ArrayBuffer ? new Uint8Array(first) : new Uint8Array(first.buffer, first.byteOffset, first.byteLength);
  return bytes.length === 32 ? new Uint8Array(bytes) : null;
}

/** Client extension results for the relay: `prf.enabled` survives, the PRF output never leaves the page. */
function publicExtensions(cred: PublicKeyCredential): Json {
  const ext = { ...extensionResults(cred) } as Json;
  if (ext.prf) ext.prf = ext.prf.enabled === undefined ? {} : { enabled: ext.prf.enabled };
  return ext;
}

const prfExtension = (salt: Bytes) => ({ prf: { eval: { first: salt } } }) as AuthenticationExtensionsClientInputs;

function creationOptions(json: Json, salt: Bytes): PublicKeyCredentialCreationOptions {
  return {
    ...json,
    challenge: fromB64u(json.challenge),
    user: { ...json.user, id: fromB64u(json.user.id) },
    excludeCredentials: (json.excludeCredentials ?? []).map((c: Json) => ({ ...c, id: fromB64u(c.id) })),
    extensions: { ...(json.extensions ?? {}), credProps: true, ...prfExtension(salt) },
  } as unknown as PublicKeyCredentialCreationOptions;
}

function requestOptions(json: Json, salt: Bytes): PublicKeyCredentialRequestOptions {
  return {
    ...json,
    challenge: fromB64u(json.challenge),
    allowCredentials: (json.allowCredentials ?? []).map((c: Json) => ({ ...c, id: fromB64u(c.id) })),
    extensions: { ...(json.extensions ?? {}), ...prfExtension(salt) },
  } as unknown as PublicKeyCredentialRequestOptions;
}

function registrationJSON(cred: PublicKeyCredential): Json {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: toB64u(new Uint8Array(cred.rawId)),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: publicExtensions(cred),
    response: {
      clientDataJSON: toB64u(new Uint8Array(r.clientDataJSON)),
      attestationObject: toB64u(new Uint8Array(r.attestationObject)),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
    },
  };
}

function authenticationJSON(cred: PublicKeyCredential): Json {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: toB64u(new Uint8Array(cred.rawId)),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: publicExtensions(cred),
    response: {
      clientDataJSON: toB64u(new Uint8Array(r.clientDataJSON)),
      authenticatorData: toB64u(new Uint8Array(r.authenticatorData)),
      signature: toB64u(new Uint8Array(r.signature)),
      userHandle: r.userHandle ? toB64u(new Uint8Array(r.userHandle)) : undefined,
    },
  };
}

/** Plain words for what WebAuthn throws. */
export function passkeyMessage(e: unknown): string {
  if (e instanceof PasskeyError) return e.message;
  const name = e instanceof DOMException ? e.name : '';
  switch (name) {
    case 'NotAllowedError':
      return 'The passkey request was cancelled or timed out.';
    case 'InvalidStateError':
      return 'This authenticator already has a passkey for this account.';
    case 'NotSupportedError':
      return 'This browser can’t make a passkey that Five Rooms accepts.';
    case 'SecurityError':
      return 'Passkeys only work on the Five Rooms site itself.';
    case 'AbortError':
      return 'The passkey request was cancelled.';
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * Makes a passkey from the relay's creation options. Many authenticators turn PRF on at creation but only evaluate
 * it when asked to sign: then one more prompt asks for the output. That assertion uses a challenge made here and is
 * never sent to the relay; it only reads the PRF output.
 *
 * The prompt comes whenever the browser knows the extension, whatever `prf.enabled` says: Windows Hello answers
 * `enabled: false` at creation and gives the output when signing. Only a browser that returns no `prf` result at all
 * is spared it. The passkey already exists by then, so a cancelled or failed prompt leaves it without PRF (the vault
 * gets no passkey copy and the recovery key opens it) rather than losing the registration.
 */
export async function createPasskey(json: Json): Promise<Ceremony> {
  if (!passkeysAvailable()) throw new PasskeyError('This browser doesn’t support passkeys.');
  const salt = await prfSalt();
  const cred = (await navigator.credentials.create({ publicKey: creationOptions(json, salt) })) as PublicKeyCredential | null;
  if (!cred) throw new PasskeyError('No passkey was made.');
  let prf = prfOutput(cred);
  if (!prf && extensionResults(cred).prf) prf = await probePrf(cred, json.rp?.id, salt);
  return { response: registrationJSON(cred), credentialId: cred.id, prf };
}

async function probePrf(cred: PublicKeyCredential, rpId: string | undefined, salt: Bytes): Promise<Bytes | null> {
  try {
    const again = (await navigator.credentials.get({
      publicKey: {
        challenge: random(32),
        rpId,
        allowCredentials: [{ type: 'public-key', id: cred.rawId }],
        userVerification: 'required',
        extensions: prfExtension(salt),
      },
    })) as PublicKeyCredential | null;
    return again ? prfOutput(again) : null;
  } catch {
    return null;
  }
}

/** Signs in with a (discoverable) passkey from the relay's request options, asking for the PRF output. */
export async function usePasskey(json: Json): Promise<Ceremony> {
  if (!passkeysAvailable()) throw new PasskeyError('This browser doesn’t support passkeys.');
  const cred = (await navigator.credentials.get({ publicKey: requestOptions(json, await prfSalt()) })) as PublicKeyCredential | null;
  if (!cred) throw new PasskeyError('No passkey was chosen.');
  return { response: authenticationJSON(cred), credentialId: cred.id, prf: prfOutput(cred) };
}
